import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { FortuneWheel } from "../components/FortuneWheel";
import { GiveawayWheel } from "../components/GiveawayWheel";
import { playPop, initAudio, audioUnlocked } from "../lib/sounds";
import { useLang } from "../i18n";

// Overlay OBS della ruota giveaway: niente menu, footer o sfondo — solo la ruota.
// Uso OBS: Browser Source -> https://<dominio>/giveaway/obs (dimensioni consigliate 900x900)
// Parametri: ?bg=dark (sfondo scuro invece di trasparente)
// Audio: in OBS abilita "Control audio via OBS" sulla sorgente per sentire i suoni.
export default function GiveawayObs() {
  const [params] = useSearchParams();
  const solid = params.get("bg") === "dark";
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [newJoiner, setNewJoiner] = useState(null);
  const prevNames = useRef(null);

  // sfondo trasparente per la cattura OBS (stesso meccanismo di /promo)
  useEffect(() => {
    if (solid) return undefined;
    const appEl = document.querySelector(".App");
    const noiseEl = document.querySelector(".noise-overlay");
    const prevBody = document.body.style.background;
    const prevApp = appEl?.style.background || "";
    const prevNoise = noiseEl?.style.display || "";
    document.body.style.setProperty("background", "transparent", "important");
    document.documentElement.style.setProperty("background", "transparent", "important");
    appEl?.style.setProperty("background", "transparent", "important");
    if (noiseEl) noiseEl.style.display = "none";
    return () => {
      document.body.style.background = prevBody;
      document.documentElement.style.background = "";
      if (appEl) appEl.style.background = prevApp;
      if (noiseEl) noiseEl.style.display = prevNoise;
    };
  }, [solid]);

  useEffect(() => {
    const load = () => api.get("/giveaways").then(({ data }) => {
      setData(data);
      // nuova iscrizione mentre l'overlay e' aperto -> sparkle + nome in grande
      const names = (data?.entries_list || []).map((e) => e.login);
      if (prevNames.current !== null) {
        const seen = new Set(prevNames.current);
        const added = names.filter((l) => !seen.has(l));
        if (added.length) {
          playPop();
          setNewJoiner(added[added.length - 1]);
        }
      }
      prevNames.current = names;
    }).catch(() => {});
    load();
    const iv = setInterval(load, 3000);
    // OBS Browser Source permette l'autoplay audio senza gesto: tentativo
    // automatico all'apertura e poi a intervalli finche' il contesto non parte
    initAudio();
    const ivAudio = setInterval(() => {
      initAudio();
      if (audioUnlocked()) clearInterval(ivAudio);
    }, 2000);
    const unlock = () => initAudio();
    window.addEventListener("pointerdown", unlock);
    return () => { clearInterval(iv); clearInterval(ivAudio); window.removeEventListener("pointerdown", unlock); };
  }, []);

  useEffect(() => {
    if (!newJoiner) return undefined;
    const tm = setTimeout(() => setNewJoiner(null), 4500);
    return () => clearTimeout(tm);
  }, [newJoiner]);

  const g = data?.active;
  const drawing = Boolean(g?.drawing && g?.winner);
  const names = (data?.entries_list || []).map((e) => e.login);

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center p-6 ${solid ? "bg-[#050505]" : ""}`} data-testid="giveaway-obs">
      {drawing ? (
        <div className="w-full max-w-[640px]">
          <GiveawayWheel
            names={names}
            winner={g.winner.login}
            startedAt={g.draw_started_at}
            durationMs={30000}
            soundOn
            onToggleSound={() => {}}
          />
        </div>
      ) : g ? (
        <div className="text-center" data-testid="obs-registration">
          <p className="hud-label mb-5">{g.title}</p>
          <FortuneWheel names={names} size={720} />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-wardogs mt-5" data-testid="obs-entries">
            {t("gv.participants")} — {data?.entries ?? 0}
          </p>
        </div>
      ) : (
        <div className="text-center" data-testid="obs-idle">
          <FortuneWheel names={[]} size={420} />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-steel mt-5">WARDOGS GIVEAWAY</p>
        </div>
      )}
      {newJoiner && (
        <div key={newJoiner} className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 chamfer-sm border border-manticore/50 bg-[#050505]/95 px-8 py-5 text-center" data-testid="obs-new-joiner">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-manticore mb-1">{t("gv.joined")}</p>
          <p className="font-display text-3xl uppercase text-white leading-none">{newJoiner}</p>
        </div>
      )}
    </div>
  );
}
